import clsx from "clsx";
import { type FC } from "react";

import { Banners } from "components/Banners";
import { DisplayCaption } from "components/DisplayCaption";
import {
  LOGO_HEADER_SIZE,
  MOBILE_OVERLAY_ROUTES,
  MOBILE_OVERLAY_ROUTE_QUERY_PARAM,
  MobileOverlayRouteType,
  TOPIC_ID_QUERY_PARAM,
} from "components/constants";
import {
  OVERLAY_TAB_MAP,
  SIDEBAR_PANEL_ICONS,
  getSidebarHeader,
  isFloatingTab,
} from "helpers/sidebar";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { useIsLgOrLarger } from "mds/hooks/use-responsive";
import { BurgerMenuIcon, ChevronLeftIcon, CrossRemoveIcon, ForumLogoIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectActivityById,
  selectCurrentCourseId,
  selectPageById,
  selectTopicById,
} from "store/selectors";
import { useHomeUrl } from "utils/hooks/use-home-url";
import {
  useBorderedPresentationColoring,
  usePresentationCaption,
  useShouldShowPresentationCaption,
} from "utils/presentation";
import { Link, useParams, useQueryParamsManager } from "utils/routing";
import { courseHomeUrl, pageUrl, useViewLevel } from "utils/urls";

/**
 * This is an alternative for the MainHeader component to be displayed on small screens.
 * It has a tiny subset of the tablet/desktop functionality, mainly removing all the
 * assessments, editing and instructor controls, breadcrumbs, and connection status,
 * but adding in mobile-related navigation functionality and menus.
 */
export const MobileMainHeader: FC = () => {
  const pathParams = useParams();
  const courseId = useAppSelector(selectCurrentCourseId);
  const { setQueryParams, deleteQueryParams, getQueryParam } = useQueryParamsManager();
  const selectedPageId = pathParams.pageId;
  const homeUrl = useHomeUrl();
  const selectedPage = useAppSelector((s) => selectPageById(s, selectedPageId));
  const activity = useAppSelector((s) => selectActivityById(s, selectedPage?.activity_id));
  const topic = useAppSelector((s) => selectTopicById(s, activity?.topic_id));

  const selectedTopicId = getQueryParam(TOPIC_ID_QUERY_PARAM) || topic?.id;
  const mobileOverlay = getQueryParam(MOBILE_OVERLAY_ROUTE_QUERY_PARAM) as MobileOverlayRouteType;
  const overlayTab = OVERLAY_TAB_MAP[mobileOverlay];
  const floatingOverlayTab = isFloatingTab(overlayTab);
  const IconComponent = SIDEBAR_PANEL_ICONS[overlayTab];
  const isMobileMainMenuOpen = mobileOverlay === MOBILE_OVERLAY_ROUTES[0];
  const showCloseButton = !floatingOverlayTab && (isMobileMainMenuOpen || overlayTab);

  const shouldShowPresentationCaption = useShouldShowPresentationCaption(true);
  const borderColorClass = useBorderedPresentationColoring();
  const { captionText, captionBgColor, linkText, linkUrl } = usePresentationCaption();

  const isLargeScreen = useIsLgOrLarger();
  const viewLevel = useViewLevel();

  const toggleMobileMainMenu = () => {
    if (!isMobileMainMenuOpen && (floatingOverlayTab || !overlayTab)) {
      setQueryParams({ [MOBILE_OVERLAY_ROUTE_QUERY_PARAM]: MOBILE_OVERLAY_ROUTES[0] });
    } else {
      deleteQueryParams([MOBILE_OVERLAY_ROUTE_QUERY_PARAM]);
    }
  };

  if (isLargeScreen) {
    console.warn("MobileMainHeader should not be used for large screens.");
  }

  function backTextAndUrl(): [string, string] {
    // const coursePageCategory = getQueryParam("category");
    // const isAssignment = isPage && coursePageCategory === "assignment";
    // const isInstructorWorkspace = isPage && coursePageCategory === "instructor_workspace";
    // const isCourseResource = isPage && coursePageCategory === "course_resource";

    // TODO: Whether we go back to "all assignments" or directly back to "home" should depend
    // on how we got there in the first place (should act similar to a back button but not affect query params).
    // This also applies to course resources, topics, etc.
    // TODO: "Back to Assignments", "Back to Resources" (Instructor Workspace), and "Back to Resources"
    //  (Course Resources) do not work anymore--we'll need to use the category param and fix the bug where we
    //  show the sidebar for the appropriate category when the category param is passed.
    // if (isAssignment && selectedPageId) {
    //   return [t("main_header.back_to_assignments"), coursePageUrl(course.id, "assignment")];
    // }
    // if (isInstructorWorkspace && selectedPageId) {
    //   return [t("main_header.back_to_resources"), coursePageUrl(course.id, "instructor_workspace")];
    // }
    // if (isCourseResource && selectedPageId) {
    //   return [t("main_header.back_to_resources"), coursePageUrl(course.id, "course_resource")];
    // }
    if (viewLevel.page && selectedTopicId && selectedPageId && courseId) {
      return [t("main_header.back_to_topic"), pageUrl(courseId, "", { topicId: selectedTopicId })];
    }

    return [t("main_header.back_to_course"), courseHomeUrl(courseId)];
  }

  const [backText, urlWithoutPage] = backTextAndUrl();

  return (
    <>
      <Banners />
      <header
        className={clsx(
          "box-content flex h-9 items-center justify-between border-b border-black-tint-90 bg-white px-4 py-2 text-black-tint-20",
          shouldShowPresentationCaption
            ? `border-b-2 ${borderColorClass}`
            : "border-b border-black-tint-90",
        )}
      >
        <div className="flex w-full justify-between">
          {overlayTab && !floatingOverlayTab ? (
            <div className="logo flex w-full items-center justify-between">
              <IconText iconStart={<IconComponent />} text={getSidebarHeader(overlayTab)} />
            </div>
          ) : viewLevel.course || viewLevel.home ? (
            <div className="logo flex items-center gap-2">
              <Link
                aria-label="Forum"
                className="flex flex-shrink-0 cursor-pointer items-center border-0 text-black lg:border"
                to={homeUrl}
              >
                <ForumLogoIcon width={LOGO_HEADER_SIZE} />
              </Link>
            </div>
          ) : (
            <div className="flex items-center justify-between truncate">
              <Link className="truncate" to={urlWithoutPage}>
                <IconText
                  iconStart={<ChevronLeftIcon />}
                  text={<span className="truncate">{backText}</span>}
                />
              </Link>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button
              className="ml-2 border-0 bg-transparent text-black"
              kind="squareLegacy"
              title={showCloseButton ? t("common.close") : t("main_nav.toggle_mobile_main_menu")}
              isSquare
              onClick={toggleMobileMainMenu}
            >
              {showCloseButton ? <CrossRemoveIcon size="l" /> : <BurgerMenuIcon />}
            </Button>
          </div>
        </div>
      </header>

      {shouldShowPresentationCaption && (
        <DisplayCaption
          bgColorClass={captionBgColor}
          caption={captionText}
          linkText={linkText}
          linkUrl={linkUrl}
        />
      )}
    </>
  );
};
